import { useMemo, useState } from 'react';
import SectionHeading from '@/components/base/SectionHeading';
import { evidenceCases, evidenceTypeTabs } from '@/mocks/homeData';

const typeStyles: Record<string, string> = {
  截图: 'bg-primary-100 text-primary-800',
  录音: 'bg-secondary-200 text-secondary-900',
  视频: 'bg-accent-100 text-accent-800',
  聊天记录: 'bg-primary-50 text-primary-700',
  文件解析: 'bg-secondary-100 text-secondary-800',
};

export default function ArchiveSection() {
  const [activeType, setActiveType] = useState('全部');

  const filtered = useMemo(() => {
    if (activeType === '全部') return evidenceCases;
    return evidenceCases.filter((item) => item.type === activeType);
  }, [activeType]);

  const featured = filtered[0];
  const rest = filtered.slice(1);

  return (
    <section id="archive" className="w-full bg-background-50 py-16 md:py-24 scroll-mt-20">
      <div className="w-full px-4 md:px-10">
        <SectionHeading
          eyebrow="Evidence Archive"
          title="证据归档库：瓜田黑料实锤证据，一眼看清每条实锤"
          description="这里的瓜田黑料实锤证据按类型分区存放，截图、录音、视频、瓜田黑料聊天记录与文件解析各自成链。每张卡片都标注来源、入库时间与文件数量，来源不明的会被明确标记。"
          right={
            <div className="flex items-center gap-3 text-sm text-foreground-600">
              <span className="w-10 h-10 flex items-center justify-center rounded-md bg-primary-100 text-primary-700 text-lg">
                <i className="ri-links-line" />
              </span>
              归档编号已与时间线锤互相关联
            </div>
          }
        />

        <div className="mt-8 md:mt-10 flex flex-wrap items-center gap-2 md:gap-3">
          <div className="px-1 py-1 rounded-full bg-background-100 border border-background-200/70 flex flex-wrap gap-1">
            {evidenceTypeTabs.map((tab) => {
              const isActive = tab.key === activeType;
              return (
                <button
                  key={tab.key}
                  type="button"
                  onClick={() => setActiveType(tab.key)}
                  className={`flex items-center gap-2 px-3 md:px-4 py-2 rounded-full text-xs md:text-sm transition-colors cursor-pointer whitespace-nowrap ${
                    isActive
                      ? 'bg-primary-500 text-background-50'
                      : 'text-foreground-600 hover:text-primary-700 hover:bg-primary-100/60'
                  }`}
                >
                  <i className={tab.icon} />
                  {tab.label}
                  <span
                    className={`text-[10px] md:text-xs ${
                      isActive ? 'text-background-100/80' : 'text-foreground-400'
                    }`}
                  >
                    {tab.count}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {filtered.length === 0 ? (
          <div className="mt-10 rounded-lg border border-dashed border-background-300 bg-background-100/60 px-6 py-16 text-center">
            <span className="w-14 h-14 mx-auto flex items-center justify-center rounded-full bg-background-200 text-foreground-500 text-2xl">
              <i className="ri-inbox-archive-line" />
            </span>
            <p className="mt-4 text-base font-medium text-foreground-800">
              这一类证据暂时还没有新入库
            </p>
            <p className="mt-2 text-sm text-foreground-500">
              换一个类型看看，或者回到「全部证据」浏览已存档内容。
            </p>
          </div>
        ) : (
          <div className="mt-8 grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6">
            {featured ? (
              <article className="lg:col-span-2 group rounded-lg overflow-hidden border border-background-200/70 bg-background-100/60 cursor-pointer">
                <div className="relative w-full h-[240px] md:h-[380px] overflow-hidden">
                  <img
                    src={featured.image}
                    alt={`${featured.title} - 瓜田黑料实锤证据`}
                    title={`${featured.title} · 瓜田黑料实锤证据存档`}
                    className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-[1.03]"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/85 via-foreground-950/25 to-transparent" />
                  <div className="absolute top-4 left-4 flex flex-wrap items-center gap-2">
                    <span className="px-2.5 py-1 rounded-md bg-accent-500 text-background-50 text-xs whitespace-nowrap">
                      今日头条实锤
                    </span>
                    <span className="px-2.5 py-1 rounded-md bg-background-50/90 text-foreground-800 text-xs whitespace-nowrap flex items-center gap-1">
                      <i className="ri-image-2-line" />
                      {featured.type}
                    </span>
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 p-4 md:p-6">
                    <h3 className="text-lg md:text-2xl font-bold text-background-50 leading-snug">
                      {featured.title}
                    </h3>
                    <div className="mt-3 flex flex-wrap items-center gap-3 text-xs md:text-sm text-background-100/85">
                      <span className="flex items-center gap-1">
                        <i className="ri-user-voice-line" />
                        {featured.source}
                      </span>
                      <span className="flex items-center gap-1">
                        <i className="ri-attachment-2" />
                        {featured.files} 份文件
                      </span>
                      <span className="flex items-center gap-1">
                        <i className="ri-fire-line" />
                        {featured.heat.toLocaleString()} 热度
                      </span>
                    </div>
                  </div>
                </div>
                <div className="p-4 md:p-6">
                  <p className="text-sm md:text-base leading-relaxed text-foreground-600">
                    {featured.summary}
                  </p>
                  <div className="mt-4 pt-4 border-t border-background-200/70 flex flex-wrap items-center justify-between gap-3">
                    <div className="flex flex-wrap items-center gap-2">
                      <span
                        className={`px-2.5 py-1 rounded-md text-xs whitespace-nowrap ${typeStyles[featured.type]}`}
                      >
                        {featured.type}
                      </span>
                      <span
                        className={`px-2.5 py-1 rounded-md text-xs whitespace-nowrap flex items-center gap-1 ${
                          featured.verified
                            ? 'bg-primary-100 text-primary-800'
                            : 'bg-secondary-200 text-secondary-900'
                        }`}
                      >
                        <i className={featured.verified ? 'ri-verified-badge-line' : 'ri-error-warning-line'} />
                        {featured.verified ? '已核验' : '待核验'}
                      </span>
                    </div>
                    <span className="text-xs text-foreground-500 flex items-center gap-1">
                      <i className="ri-time-line" />
                      入库 {featured.time}
                    </span>
                  </div>
                </div>
              </article>
            ) : null}

            <div className="lg:col-span-1 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 gap-4 md:gap-6">
              {rest.slice(0, 2).map((item) => (
                <article
                  key={item.id}
                  className="group rounded-lg overflow-hidden border border-background-200/70 bg-background-100/60 cursor-pointer"
                >
                  <div className="relative w-full h-[160px] md:h-[150px] overflow-hidden">
                    <img
                      src={item.image}
                      alt={`${item.title} - 瓜田黑料实锤证据`}
                      title={`${item.title} · 瓜田黑料实锤证据存档`}
                      className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-[1.03]"
                    />
                    <span className="absolute top-3 left-3 px-2.5 py-1 rounded-md bg-background-50/90 text-foreground-800 text-xs whitespace-nowrap">
                      {item.type}
                    </span>
                  </div>
                  <div className="p-4">
                    <h3 className="text-sm md:text-base font-semibold text-foreground-950 leading-snug line-clamp-2">
                      {item.title}
                    </h3>
                    <div className="mt-3 flex flex-wrap items-center gap-2 text-xs text-foreground-500">
                      <span className="flex items-center gap-1">
                        <i className="ri-attachment-2" />
                        {item.files}
                      </span>
                      <span className="flex items-center gap-1">
                        <i className="ri-fire-line" />
                        {item.heat.toLocaleString()}
                      </span>
                      <span className="flex items-center gap-1">
                        <i className="ri-time-line" />
                        {item.time.slice(5)}
                      </span>
                    </div>
                  </div>
                </article>
              ))}
            </div>

            {rest.slice(2).map((item) => (
              <article
                key={item.id}
                className="group rounded-lg overflow-hidden border border-background-200/70 bg-background-100/60 cursor-pointer flex flex-col"
              >
                <div className="relative w-full h-[170px] md:h-[190px] overflow-hidden">
                  <img
                    src={item.image}
                    alt={`${item.title} - 瓜田黑料实锤证据`}
                    title={`${item.title} · 瓜田黑料实锤证据存档`}
                    className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-[1.03]"
                  />
                  <span
                    className={`absolute top-3 left-3 px-2.5 py-1 rounded-md text-xs whitespace-nowrap ${typeStyles[item.type]}`}
                  >
                    {item.type}
                  </span>
                </div>
                <div className="p-4 md:p-5 flex flex-col flex-1">
                  <h3 className="text-sm md:text-base font-semibold text-foreground-950 leading-snug">
                    {item.title}
                  </h3>
                  <p className="mt-2 text-xs md:text-sm leading-relaxed text-foreground-600 line-clamp-3">
                    {item.summary}
                  </p>
                  <div className="mt-4 pt-3 border-t border-background-200/70 flex flex-wrap items-center justify-between gap-2 text-xs text-foreground-500">
                    <span
                      className={`px-2 py-0.5 rounded-md whitespace-nowrap ${
                        item.verified ? 'bg-primary-100 text-primary-800' : 'bg-secondary-200 text-secondary-900'
                      }`}
                    >
                      {item.verified ? '已核验' : '待核验'}
                    </span>
                    <span className="flex items-center gap-1">
                      <i className="ri-fire-line" />
                      {item.heat.toLocaleString()}
                    </span>
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}